import Button from "@ui/button/Button";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GD } from "src/GD";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { setIssuerRequestContext } from "src/services/issuerRequestContext";
import { getIssuerDisplayName } from "./issuerIdentity";

type IssuerContextValue = {
	issuers: ICompanyIssuer[];
	selectedIssuer: ICompanyIssuer | null;
	selectedIssuerId: number | null;
	loading: boolean;
	balanceSyncing: boolean;
	error: string | null;
	scopeKey: string;
	selectIssuer: (issuer: number) => void;
	refreshIssuers: (withBuyers?: boolean) => Promise<boolean>;
	setBalanceSyncing: (syncing: boolean) => void;
};

const IssuerContext = createContext<IssuerContextValue | null>(null);
const ISSUER_SELECTION_STORAGE_PREFIX = "cardpartner:selected-issuer";

const issuerSelectionStorageKey = (companyUid: string) =>
	`${ISSUER_SELECTION_STORAGE_PREFIX}:${companyUid}`;

const readStoredIssuer = (companyUid: string | null) => {
	if (!companyUid) return null;
	try {
		const issuer = Number(localStorage.getItem(issuerSelectionStorageKey(companyUid)));
		return Number.isInteger(issuer) && issuer > 0 ? issuer : null;
	} catch {
		return null;
	}
};

const storeIssuer = (companyUid: string | null, issuer: number) => {
	if (!companyUid) return;
	try {
		localStorage.setItem(issuerSelectionStorageKey(companyUid), String(issuer));
	} catch {
		// Issuer switching still works when browser storage is unavailable.
	}
};

const clearStoredIssuer = (companyUid: string | null) => {
	if (!companyUid) return;
	try {
		localStorage.removeItem(issuerSelectionStorageKey(companyUid));
	} catch {
		// Storage cleanup is best-effort only.
	}
};

const normalizeIssuers = (value: unknown): ICompanyIssuer[] => {
	if (!Array.isArray(value)) return [];
	const seen = new Set<number>();
	const result: ICompanyIssuer[] = [];
	for (const item of value) {
		if (!item || typeof item !== "object") continue;
		const raw = item as Partial<ICompanyIssuer>;
		const issuer = Number(raw.issuer);
		const balance = Number(raw.balance);
		const buyersBalance = raw.buyers_balance === undefined ? undefined : Number(raw.buyers_balance);
		if (
			!Number.isInteger(issuer) ||
			issuer <= 0 ||
			seen.has(issuer) ||
			typeof raw.currency !== "string" ||
			!raw.currency.trim() ||
			!Number.isFinite(balance) ||
			(buyersBalance !== undefined && !Number.isFinite(buyersBalance))
		) continue;
		seen.add(issuer);
		result.push({
			issuer,
			name: getIssuerDisplayName({ issuer, issuer_label: raw.issuer_label, name: raw.name }),
			is_default: raw.is_default === true,
			currency: raw.currency,
			balance,
			...(buyersBalance === undefined ? {} : { buyers_balance: buyersBalance }),
			crypto_topup: raw.crypto_topup === true,
		});
	}
	return result;
};

export const IssuerProvider = ({
	companyUid,
	sessionKey,
	children,
}: {
	companyUid: string | null;
	sessionKey: string | number;
	children: ReactNode;
}) => {
	const [issuers, setIssuers] = useState<ICompanyIssuer[]>([]);
	const [selectedIssuerId, setSelectedIssuerId] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [balanceSyncing, setBalanceSyncing] = useState(false);
	const [ready, setReady] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [revision, setRevision] = useState(0);
	const requestSequence = useRef(0);
	const selectedIssuerIdRef = useRef<number | null>(null);

	const applySelection = useCallback((nextIssuers: ICompanyIssuer[]) => {
		const current = selectedIssuerIdRef.current;
		const stored = readStoredIssuer(companyUid);
		if (stored !== null && !nextIssuers.some(item => item.issuer === stored)) {
			clearStoredIssuer(companyUid);
		}
		const selected =
			nextIssuers.find(item => item.issuer === current) ??
			nextIssuers.find(item => item.issuer === stored) ??
			nextIssuers.find(item => item.is_default) ??
			nextIssuers[0] ??
			null;
		const nextId = selected?.issuer ?? null;
		selectedIssuerIdRef.current = nextId;
		setIssuerRequestContext(companyUid, nextId);
		setIssuers(nextIssuers);
		setSelectedIssuerId(nextId);
	}, [companyUid]);

	const refreshIssuers = useCallback(async (withBuyers = false) => {
		const sequence = ++requestSequence.current;
		setLoading(true);
		try {
			const response = await GD.REQ_COMPANY_ISSUERS.request({ with_buyers: withBuyers });
			if (sequence !== requestSequence.current) return false;
			if (response.error) {
				setError(getFriendlyErrorMessage(response.error, "Could not load issuer accounts"));
				return false;
			}
			applySelection(normalizeIssuers(response.data));
			setError(null);
			return true;
		} catch (requestError) {
			if (sequence !== requestSequence.current) return false;
			setError(getFriendlyErrorMessage(requestError, "Could not load issuer accounts"));
			return false;
		} finally {
			if (sequence === requestSequence.current) {
				setLoading(false);
				setReady(true);
			}
		}
	}, [applySelection]);

	useEffect(() => {
		requestSequence.current += 1;
		selectedIssuerIdRef.current = null;
		setIssuerRequestContext(companyUid, null);
		setIssuers([]);
		setSelectedIssuerId(null);
		setError(null);
		setBalanceSyncing(false);
		setReady(false);
		setRevision(0);
		if (!companyUid) {
			setLoading(false);
			setReady(true);
			return;
		}
		void refreshIssuers();
		return () => {
			requestSequence.current += 1;
		};
	}, [companyUid, refreshIssuers, sessionKey]);

	useEffect(() => {
		const refreshSignal = GD.S_COMPANY_ISSUERS_REFRESH;
		if (!refreshSignal) return;
		const subscription = refreshSignal.subscribe(() => {
			void refreshIssuers();
		});
		return () => {
			refreshSignal.unsubscribe(subscription);
		};
	}, [refreshIssuers]);

	const selectIssuer = useCallback((issuer: number) => {
		if (issuer === selectedIssuerIdRef.current || !issuers.some(item => item.issuer === issuer)) return;
		selectedIssuerIdRef.current = issuer;
		setIssuerRequestContext(companyUid, issuer);
		storeIssuer(companyUid, issuer);
		setSelectedIssuerId(issuer);
		setRevision(value => value + 1);
		GD.S_ISSUER_CHANGED.invoke(issuer);
	}, [companyUid, issuers]);

	const selectedIssuer = issuers.find(item => item.issuer === selectedIssuerId) ?? null;
	const value = useMemo<IssuerContextValue>(() => ({
		issuers,
		selectedIssuer,
		selectedIssuerId,
		loading,
		balanceSyncing,
		error,
		scopeKey: `${companyUid ?? "no-company"}:${selectedIssuerId ?? "default"}:${revision}`,
		selectIssuer,
		refreshIssuers,
		setBalanceSyncing,
	}), [balanceSyncing, companyUid, error, issuers, loading, refreshIssuers, revision, selectIssuer, selectedIssuer, selectedIssuerId]);

	return (
		<IssuerContext.Provider value={value}>
			{ready && error && issuers.length === 0 ? (
				<div style={{ width: "100%", minHeight: "100dvh", display: "grid", placeItems: "center", padding: "24px", boxSizing: "border-box", color: "#334155", background: "#F8FAFC", fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
					<div>
						<div style={{ fontSize: "1.05rem", fontWeight: 750 }}>Issuer accounts could not be loaded</div>
						<div style={{ marginTop: "7px", maxWidth: "420px", color: "#64748B", fontSize: ".88rem" }}>{error}</div>
						<Button onClick={() => void refreshIssuers()} sx={{ marginTop: "18px", minHeight: "42px", padding: "0 18px", border: "1px solid #0F8D69", borderRadius: "10px", color: "#FFFFFF", background: "#0F8D69", font: "inherit", fontWeight: 700, cursor: "pointer" }}>Retry</Button>
					</div>
				</div>
			) : ready ? children : (
				<div className="boot-loader" role="status">Loading issuer accounts…</div>
			)}
		</IssuerContext.Provider>
	);
};

// The hooks intentionally share the provider's private context in one module.
// eslint-disable-next-line react-refresh/only-export-components
export const useIssuer = () => {
	const value = useContext(IssuerContext);
	if (!value) throw new Error("useIssuer must be used inside IssuerProvider");
	return value;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalIssuer = () => useContext(IssuerContext);
